/**
 * Demo missions and requirements for ?mock=1, over the shared mock fixture's projects and
 * sessions, and a tiny in-browser stand-in for the hub's missions API so every control works
 * without a hub. The rules mirror apps/hub/src/ext/missions (same validation and helpers from
 * @orgus/protocol).
 */
import {
  ITEM_WORKFLOW,
  canonicalLink,
  fitStatus,
  formatKey,
  initialStatus,
  itemInputError,
  keyPrefixFor,
  kindWord,
  linkInputError,
  missionInputError,
  newPrefixedId,
  normalizeCriteria,
  normalizeKind,
  statusLabel,
} from '@orgus/protocol';
import type {
  AcceptanceInput,
  BacklogItem,
  DefectSignal,
  Id,
  ItemInput,
  ItemLink,
  ItemPatch,
  LinkInput,
  LinkType,
  Mission,
  MissionInput,
  MissionLaunch,
  MissionPatch,
  MissionsState,
  ProjectKey,
  Proposal,
  ProposedItem,
  SavedView,
  SavedViewInput,
  SourceSection,
} from '@orgus/protocol';
import { MOCK_BLOCKED, MOCK_FAILED, MOCK_IDS } from '../../lib/mock.ts';
import { runDemoItems, runDemoMissions, runDemoReview } from './run/mock.ts';

const MIN = 60_000;
const H = 60 * MIN;
const D = 24 * H;

const gwt = (given: string, when: string, then: string, done = false): AcceptanceInput => ({ format: 'gwt', text: '', given, when, then, done });
const check = (text: string, done = false): AcceptanceInput => ({ format: 'check', text, done });

type ItemExtra = Partial<Omit<BacklogItem, 'acceptance'>> & { acceptance?: AcceptanceInput[] };

function item(id: string, key: string, projectId: Id, kind: BacklogItem['kind'], title: string, status: BacklogItem['status'], priority: BacklogItem['priority'], ago: number, extra: ItemExtra = {}, now: number): BacklogItem {
  const { acceptance, ...rest } = extra;
  return {
    id,
    key,
    projectId,
    kind,
    title,
    body: '',
    status,
    priority,
    owner: 'srikanth',
    parentId: null,
    rank: 0,
    estimate: null,
    labels: [],
    components: [],
    defect: kind === 'defect' ? { severity: 'major', environment: '', steps: '', expected: '', actual: '' } : null,
    source: null,
    aiDrafted: false,
    statusNote: null,
    statusPinned: false,
    createdAt: now - ago - 3 * D,
    updatedAt: now - ago,
    ...rest,
    acceptance: normalizeCriteria(acceptance ?? []),
  };
}

function mission(id: string, projectId: Id, itemId: Id | null, title: string, goal: string, ago: number, extra: Partial<Mission>, now: number): Mission {
  return {
    id,
    itemId,
    projectId,
    title,
    goal,
    owner: 'owner',
    state: 'planned',
    progress: 0,
    sessionIds: [],
    launches: [],
    placement: {},
    model: null,
    budget: null,
    closed: null,
    syncedState: null,
    report: null,
    sequenceId: null,
    createdAt: now - ago - H,
    updatedAt: now - ago,
    ...extra,
  };
}

function launch(machineId: Id, at: number, sessionId: Id | null, extra: Partial<MissionLaunch> = {}): MissionLaunch {
  return { id: `au_demo_${sessionId ?? at}`, machineId, at, by: 'owner', mode: 'headless', model: 'claude-opus-5', checkoutId: null, cwd: null, worktree: null, sessionId, result: 'ok', ...extra };
}

export const DEMO = {
  approvalsEpic: 'itm_demo_approvals',
  routingFeature: 'itm_demo_routing',
  requestCard: 'itm_demo_request_card',
  inbox: 'itm_demo_inbox',
  delegates: 'itm_demo_delegates',
  missionsEpic: 'itm_demo_missions',
  reqEpic: 'itm_demo_requirements',
  importFeature: 'itm_demo_import',
  stripe: 'itm_demo_stripe',
  approvalsMission: 'msn_demo_approvals',
  foundationMission: 'msn_demo_foundation',
  importProposal: 'prp_demo_import',
} as const;

const SPEC_SECTIONS: SourceSection[] = [
  { id: 'S1', title: 'Payments', level: 1, text: 'Customers pay for orders by card or bank transfer. Every payment is recorded against its order.' },
  { id: 'S1.1', title: 'Card payments', level: 2, text: 'Cards are charged when the order is placed. A declined card shows the reason the bank gave and lets the customer try another card.' },
  { id: 'S1.2', title: 'Refunds', level: 2, text: 'Support can refund a paid order in full or in part within 30 days. The customer gets an email with the amount and when to expect it.' },
  { id: 'S2', title: 'Receipts', level: 1, text: 'Every paid order has a PDF receipt the customer can download from the order page. Receipts show tax per line.' },
  { id: 'S3', title: 'Reporting', level: 1, text: 'Finance needs a monthly export of payments and refunds. The format is to be agreed.' },
];

function proposed(ref: string, parentRef: string, kind: BacklogItem['kind'], title: string, body: string, sections: string[], acceptance: AcceptanceInput[] = [], extra: Partial<ProposedItem> = {}): ProposedItem {
  return { ref, parentRef, kind, title, body, acceptance, priority: 2, estimate: kind === 'story' ? 3 : null, labels: ['payments'], defect: null, sections, decision: 'pending', ...extra };
}

export function createMockMissions(now: number = Date.now()): MissionsState {
  const M = MOCK_IDS;
  const items: BacklogItem[] = [
    // Qawl: an epic with a feature, stories, tasks and defects.
    item(DEMO.approvalsEpic, 'QAWL-1', M.qawl, 'epic', 'Approvals: the right person answers, once', 'in_progress', 0, 2 * H, { rank: 1000, body: 'Every agent request reaches one inbox, the right person answers it, and they can say do not ask again.', labels: ['approvals'] }, now),
    item(DEMO.routingFeature, 'QAWL-2', M.qawl, 'feature', 'Approval routing', 'in_progress', 1, 3 * H, { parentId: DEMO.approvalsEpic, rank: 1000, body: 'Requests go to whoever can answer them: the owner, a delegate, or the on-call person. Solid arrows are the control flow (who decides next), the moving edges carry the request itself.\n\n```mermaid\nflowchart LR\n  R[Agent request] r1@--> P{Policy rule?}\n  P -- allow --> A[Answered by policy]\n  P -- ask --> O[Owner]\n  O -- away --> D[Delegate]\n  D -- away --> C[On-call]\n  O & D & C i1@--> I[Inbox: one answer, once]\n  I -.->|answer| R\n  r1@{ animate: true }\n  i1@{ animate: true }\n```' }, now),
    item(DEMO.requestCard, 'QAWL-3', M.qawl, 'story', 'Request card in the chat stream', 'in_progress', 1, 22 * MIN, {
      parentId: DEMO.routingFeature,
      rank: 1000,
      estimate: 5,
      labels: ['approvals', 'chat'],
      components: ['web'],
      body: 'As a reviewer, I want agent requests inline in the chat, so that I can answer without leaving the conversation.',
      acceptance: [
        gwt('an agent asks to run a command', 'the request arrives', 'a card shows the command, the folder and Allow once and Always allow', true),
        gwt('a card is on screen', 'I choose Always allow', 'the same command in the same folder is not asked again'),
        check('Cards render within 390 px without clipping'),
      ],
      statusNote: { text: 'Mission "Request card and inbox" is working on it.', by: 'orgus', at: now - H, kind: 'mission', missionId: DEMO.approvalsMission },
    }, now),
    item(DEMO.inbox, 'QAWL-4', M.qawl, 'story', 'One inbox for agent requests', 'ready', 1, 3 * H, {
      parentId: DEMO.routingFeature,
      rank: 2000,
      estimate: 3,
      labels: ['approvals'],
      body: 'As a reviewer, I want every waiting request in one inbox, so that nothing waits on a tab I did not open.',
      acceptance: [gwt('three sessions are waiting', 'I open the inbox', 'all three requests are listed, oldest first'), check('The inbox should load fast')],
    }, now),
    item('itm_demo_digest', 'QAWL-5', M.qawl, 'story', 'Weekly approvals digest with every decision, export to CSV, Slack and email, per team and per person, with trends', 'backlog', 2, 6 * H, {
      parentId: DEMO.approvalsEpic,
      rank: 3000,
      estimate: 13,
      labels: ['approvals', 'reporting'],
      body: 'A digest of approvals for managers. It should be easy to read and flexible.',
      acceptance: [check('Shows decisions'), check('Exports'), check('Slack'), check('Email'), check('Per team'), check('Per person'), check('Trends'), check('Filters'), check('Sorting')],
    }, now),
    item(DEMO.delegates, 'QAWL-6', M.qawl, 'defect', 'Approval routing skips delegates on weekends', 'triage', 0, 5 * H, {
      owner: null,
      labels: ['approvals'],
      defect: { severity: 'critical', environment: 'Hub 0.2.0 on Windows, policy "weekend on-call"', steps: '1. Set a delegate for Saturday\n2. On Saturday, have an agent ask to run a command\n3. Watch who is notified', expected: 'The delegate is notified and can answer.', actual: 'Only the owner is notified; the request times out after 30 minutes.' },
      acceptance: [gwt('a delegate is set for the weekend', 'a request arrives on Saturday', 'the delegate is notified within 1 minute')],
    }, now),
    item('itm_demo_scope', 'QAWL-7', M.qawl, 'task', 'Move requireScope callers off orgId in the body', 'done', 2, 26 * H, { parentId: DEMO.requestCard, estimate: 1 }, now),
    item('itm_demo_cardcss', 'QAWL-8', M.qawl, 'task', 'Card layout for narrow screens', 'in_progress', 2, 40 * MIN, { parentId: DEMO.requestCard, estimate: 2 }, now),
    item('itm_demo_pricing', 'QAWL-9', M.qawl, 'defect', 'Runs priced at $0 after the model rename', 'done', 0, 25 * H, {
      defect: { severity: 'major', environment: 'Production, since the 4.5 rename', steps: '1. Run any session\n2. Open Spend', expected: 'The run is priced from its usage rows.', actual: 'The run shows $0.00.' },
    }, now),
    item('itm_demo_voice', 'QAWL-10', M.qawl, 'defect', 'Voice mint takes 3 to 4 s on Gemini Live', 'ready', 2, 5 * H, {
      body: 'The token request waits on the config fetch. Start both together.',
      defect: { severity: 'minor', environment: '', steps: '', expected: '', actual: '' },
    }, now),
    item('itm_demo_spike', 'QAWL-11', M.qawl, 'spike', 'Can approvals reuse the policy engine rules?', 'backlog', 2, 9 * H, { parentId: DEMO.approvalsEpic, rank: 4000, estimate: 2, body: 'Time-box: one day. Find out whether policy rules can express who answers what.' }, now),

    // Orgus: requirements management, imported from a document.
    item(DEMO.reqEpic, 'AIR-1', M.orgus, 'epic', 'Requirements management', 'in_progress', 1, 30 * MIN, {
      rank: 1000,
      body: 'Epics, features, stories and defects organised as a tree, a board, a table and a readable spec. AI writes and reviews them; people decide.',
      source: { kind: 'document', name: 'requirements-v2.md', section: 'Requirements', sections: ['S1'] },
      labels: ['requirements'],
    }, now),
    item(DEMO.importFeature, 'AIR-2', M.orgus, 'feature', 'Import a spec into a reviewed breakdown', 'in_progress', 1, 30 * MIN, {
      parentId: DEMO.reqEpic,
      rank: 1000,
      body: 'Upload a Markdown, Word or PDF document; the AI proposes epics, features and stories that trace back to its sections.',
      source: { kind: 'document', name: 'requirements-v2.md', section: 'Requirements > Import', sections: ['S1.1'] },
    }, now),
    item('itm_demo_parse', 'AIR-3', M.orgus, 'story', 'Read Markdown, Word and PDF without native dependencies', 'done', 1, 2 * H, {
      parentId: DEMO.importFeature,
      rank: 1000,
      estimate: 5,
      aiDrafted: true,
      body: 'As a product owner, I want to import the document I already have, so that I do not retype it.',
      acceptance: [gwt('a .docx with heading styles', 'I import it', 'its headings become sections S1, S1.1 and so on', true), gwt('a scanned PDF', 'I import it', 'the import says no text was found and what to do', true)],
      source: { kind: 'document', name: 'requirements-v2.md', section: 'Requirements > Import > Formats', sections: ['S1.1.1'] },
    }, now),
    item('itm_demo_review', 'AIR-4', M.orgus, 'story', 'Review the proposed breakdown before anything is created', 'review', 1, 35 * MIN, {
      parentId: DEMO.importFeature,
      rank: 2000,
      estimate: 8,
      aiDrafted: true,
      body: 'As a product owner, I want to accept, edit or reject each proposed item, so that nothing lands in the backlog I did not agree to.',
      acceptance: [
        gwt('a finished import', 'I open its review', 'the document is on the left and the proposed tree on the right', true),
        gwt('a proposed story', 'I reject its parent', 'it moves to the nearest accepted ancestor', true),
        check('Applying creates only accepted items, with their source sections'),
      ],
      source: { kind: 'document', name: 'requirements-v2.md', section: 'Requirements > Import > Review', sections: ['S1.1.2'] },
      statusNote: { text: 'Mission "Review the proposed breakdown" finished. Review what it changed.', by: 'orgus', at: now - 35 * MIN, kind: 'mission', missionId: 'msn_demo_review' },
    }, now),
    item('itm_demo_trace', 'AIR-5', M.orgus, 'story', 'Trace every requirement to its document section', 'ready', 2, 4 * H, {
      parentId: DEMO.importFeature,
      rank: 3000,
      estimate: 3,
      aiDrafted: true,
      body: 'As an auditor, I want each story to show the section it came from, so that I can check coverage.',
      acceptance: [gwt('an imported story', 'I open it', 'it shows the document name and the section path')],
      source: { kind: 'document', name: 'requirements-v2.md', section: 'Requirements > Traceability', sections: ['S1.3'] },
    }, now),
    item('itm_demo_aiwrite', 'AIR-6', M.orgus, 'feature', 'AI writes and checks items', 'backlog', 1, 5 * H, { parentId: DEMO.reqEpic, rank: 2000, body: 'Draft from one line or a screenshot, improve, split, estimate and find duplicates.' }, now),
    item('itm_demo_screenshot', 'AIR-7', M.orgus, 'story', 'Draft a defect from a screenshot', 'backlog', 1, 5 * H, {
      parentId: 'itm_demo_aiwrite',
      rank: 1000,
      body: 'As a tester, I want to paste a screenshot and get a defect with repro steps, so that reporting takes seconds.',
    }, now),
    item('itm_demo_board', 'AIR-8', M.orgus, 'story', 'Backlog board and list views', 'done', 1, 35 * MIN, { parentId: DEMO.missionsEpic, estimate: 3 }, now),
    item(DEMO.missionsEpic, 'AIR-9', M.orgus, 'epic', 'Missions and backlog', 'in_progress', 1, 40 * MIN, { rank: 2000, body: 'Backlog items become missions, missions run sessions on any machine.' }, now),
    item('itm_demo_detail', 'AIR-10', M.orgus, 'story', 'Mission detail page with one progress bar', 'in_progress', 1, 20 * MIN, { parentId: DEMO.missionsEpic, estimate: 5, acceptance: [gwt('a running mission', 'I open it', 'one progress bar shows sessions finished and working')] }, now),
    item('itm_demo_light', 'AIR-11', M.orgus, 'story', 'Light theme for every page', 'backlog', 3, 2 * D, { owner: null }, now),
    item('itm_demo_light2', 'AIR-12', M.orgus, 'story', 'Light theme on every page', 'backlog', 3, 1 * D, { owner: null, parentId: DEMO.missionsEpic }, now),
    // Stopped by an approval nobody answered (R-HL-3): in progress and blocked, never in review.
    item('itm_demo_release', 'AIR-13', M.orgus, 'story', 'Publish @orgus/sdk 0.3 to npm', 'in_progress', 1, 39 * MIN, {
      body: 'Bump the version, build, tag and publish the SDK.',
      statusNote: { text: 'Mission "Publish the SDK to npm" is blocked: approval expired. Open the mission to see what it asked.', by: 'orgus', at: now - H + 10 * MIN, kind: 'mission', missionId: MOCK_BLOCKED.expiredMission },
    }, now),

    // Billing and docs.
    item(DEMO.stripe, 'BILL-1', M.billing, 'defect', 'Stripe webhooks retried without idempotency keys', 'in_progress', 0, 18 * MIN, {
      body: 'A retried webhook charged a customer twice. Carry the event id as the idempotency key.',
      defect: { severity: 'critical', environment: 'Production, billing-service 3.4', steps: '1. Deliver payment_intent.succeeded\n2. Return 500 once\n3. Let Stripe retry', expected: 'One charge.', actual: 'Two charges for the same order.' },
      acceptance: [gwt('a webhook delivered twice', 'both deliveries are processed', 'the customer is charged once'), check('An integration test covers a double delivery')],
    }, now),
    item('itm_demo_deps', 'BILL-2', M.billing, 'chore', 'Nightly dependency upgrade', 'ready', 3, 4 * H, {}, now),
    item('itm_demo_ledger', 'BILL-3', M.billing, 'defect', 'Ledger rows missing for March', 'in_progress', 0, 22 * MIN, {
      body: 'The March import skipped refunds. Backfill the ledger table and reconcile the totals.',
      defect: { severity: 'major', environment: 'Production, ledger import', steps: '1. Run the March import\n2. Compare refunds with ledger rows', expected: 'Every refund has a ledger row.', actual: 'Refunds are missing from the ledger.' },
      acceptance: [check('Every March refund has a ledger row'), check('The March totals reconcile with the import')],
      statusNote: { text: 'Mission "Backfill the March ledger" is blocked: approval denied. Open the mission to see what it asked.', by: 'orgus', at: now - H, kind: 'mission', missionId: MOCK_BLOCKED.deniedMission },
    }, now),
    item('itm_demo_guide', 'DOCS-1', M.docs, 'story', 'Getting started guide for v2', 'done', 2, 2 * H, {}, now),
    item('itm_demo_openapi', 'DOCS-2', M.docs, 'story', 'API reference from the OpenAPI spec', 'in_progress', 2, 14 * MIN, {}, now),
    item('itm_demo_search', 'DOCS-3', M.docs, 'story', 'Search across the docs', 'backlog', 3, 3 * D, { owner: null }, now),
    // Missions home: work running now, and finished work waiting for a person to judge it.
    item('itm_demo_sessions_page', 'AIR-14', M.orgus, 'story', 'Sessions page with grouped rows and a drawer', 'in_progress', 1, 26 * MIN, {
      parentId: DEMO.missionsEpic,
      estimate: 3,
      acceptance: [check('Rows group by state'), check('The drawer opens on Enter')],
    }, now),
    item('itm_demo_sdk_v18', 'BILL-4', M.billing, 'story', 'Upgrade the payments SDK to v18', 'review', 2, 5 * H + 40 * MIN, {
      estimate: 2,
      acceptance: [check('The SDK is on v18 with no type errors'), check('Webhook tests pass against v18'), check('The changelog names the upgrade')],
      statusNote: { text: 'Mission "Upgrade the payments SDK" finished. Review what it changed.', by: 'orgus', at: now - 5 * H - 40 * MIN, kind: 'mission', missionId: 'msn_demo_sdk_v18' },
    }, now),
  ];

  const links: ItemLink[] = [
    { id: 'lnk_demo_1', type: 'affects', from: DEMO.delegates, to: DEMO.routingFeature, by: 'srikanth', at: now - 5 * H },
    { id: 'lnk_demo_2', type: 'blocks', from: DEMO.requestCard, to: DEMO.inbox, by: 'srikanth', at: now - 3 * H },
    { id: 'lnk_demo_3', type: 'implements', from: 'itm_demo_review', to: 'itm_demo_parse', by: 'srikanth', at: now - 2 * H },
    { id: 'lnk_demo_4', type: 'relates', from: 'itm_demo_spike', to: DEMO.routingFeature, by: 'srikanth', at: now - 9 * H },
  ];

  const missions: Mission[] = [
    mission(DEMO.approvalsMission, M.qawl, DEMO.requestCard, 'Request card and inbox', 'Agent requests render inline in chat and land in one inbox; the routing test passes.', 3 * MIN, {
      sessionIds: ['b1f4c2e0-approvals', 'c3d8a7f1-request-card'],
      launches: [launch(M.studio, now - 2 * H - 10 * MIN, 'b1f4c2e0-approvals', { checkoutId: 'c-qawl-main' }), launch(M.studio, now - H - 5 * MIN, 'c3d8a7f1-request-card', { checkoutId: 'c-qawl-main', worktree: 'C:/tmp/qawl-wt-approvals' })],
      placement: { machineId: M.studio },
      budget: 25,
      model: 'claude-opus-5',
      syncedState: 'running',
    }, now),
    mission(DEMO.foundationMission, M.orgus, 'itm_demo_detail', 'Build the Orgus foundation', 'Protocol, design system and app shell are in, and every page renders on demo data.', 3 * 1000, {
      sessionIds: ['e7a90d13-foundation', 'f0c9e1a2-sessions-page'],
      launches: [launch(M.studio, now - H - 32 * MIN, 'e7a90d13-foundation', { checkoutId: 'c-fd-main' }), launch(M.studio, now - 26 * MIN, 'f0c9e1a2-sessions-page', { checkoutId: 'c-fd-main' })],
      placement: { machineId: M.studio },
      budget: 40,
    }, now),
    mission('msn_demo_stripe', M.billing, DEMO.stripe, 'Retry Stripe webhooks safely', 'Retries carry the event id as the idempotency key; integration tests cover a double delivery.', 7 * MIN, {
      sessionIds: ['7b3e5d20-stripe-retry', '9d1a4c6e-webhook-tests'],
      launches: [launch(M.mbp, now - 3 * H, '7b3e5d20-stripe-retry'), launch(M.mbp, now - 52 * MIN, '9d1a4c6e-webhook-tests', { worktree: '/Users/srikanth/code/billing-service-wt/stripe-retry' })],
      placement: { machineId: M.mbp },
      budget: 10,
      budgetHardStop: true,
    }, now),
    // QA-63: opted in to a hard budget and passed it, so Orgus stopped it.
    mission('msn_demo_budget_stop', M.billing, null, 'Profile the invoice export', 'The monthly invoice export runs in under 30 seconds for the largest customer.', 48 * MIN, {
      launches: [launch(M.mbp, now - 2 * H, null, { model: 'claude-opus-5' })],
      placement: { machineId: M.mbp },
      budget: 5,
      budgetHardStop: true,
      closed: { as: 'stopped', at: now - 48 * MIN, by: 'orgus', note: 'Over its budget of $5.00.', reason: 'budget' },
    }, now),
    // Stopped by approvals (R-HL-3): never Done. features/approvals/mock.ts has the requests.
    mission(MOCK_BLOCKED.expiredMission, M.orgus, 'itm_demo_release', 'Publish the SDK to npm', 'Version 0.3.0 of @orgus/sdk is on npm with a changelog entry and a git tag.', 39 * MIN, {
      sessionIds: [MOCK_BLOCKED.expiredSession],
      launches: [launch(M.studio, now - H - 5 * MIN, MOCK_BLOCKED.expiredSession, { checkoutId: 'c-fd-main' })],
      placement: { machineId: M.studio },
    }, now),
    // A model that does not exist (ADR 0059): two runs, both failed in 3 s; the drawer retries with Haiku 5.5.
    mission(MOCK_FAILED.mission, M.orgus, null, 'add() subtracts instead of adding', 'add(2, 3) returns 5 and the unit test covers it.', 2 * H, {
      sessionIds: [...MOCK_FAILED.modelRuns],
      launches: MOCK_FAILED.modelRuns.map((id, i) => launch(M.studio, now - (3 - i) * H - 3_000, id, { model: 'claude-haiku-5', cwd: MOCK_FAILED.folder })),
      placement: { machineId: M.studio },
      model: 'claude-haiku-5',
    }, now),
    mission(MOCK_BLOCKED.deniedMission, M.billing, 'itm_demo_ledger', 'Backfill the March ledger', 'Every March refund has a ledger row and the monthly totals reconcile.', 22 * MIN, {
      sessionIds: [MOCK_BLOCKED.deniedSession],
      launches: [launch(M.mbp, now - H - 20 * MIN, MOCK_BLOCKED.deniedSession, { model: 'claude-sonnet-5' })],
      placement: { machineId: M.mbp },
      budget: 6,
      // The owner redirected it to staging; the session followed and reported. Blocked, so this is
      // the session's own report: it checked nothing off (R-HL-3).
      report: {
        at: now - 22 * MIN,
        text: 'Ran the backfill on staging as you asked. ACCEPTANCE REPORT AC-1: PASS - staging has 1,284 refunds and 1,284 ledger rows - AC-2: PASS - the March totals match the import to the cent',
        acceptance: [
          { id: 'ac1', met: true, evidence: 'staging has 1,284 refunds and 1,284 ledger rows' },
          { id: 'ac2', met: true, evidence: 'the March totals match the import to the cent' },
        ],
        blocked: true,
      },
    }, now),
    mission('msn_demo_openapi', M.docs, 'itm_demo_openapi', 'Generate the API reference', 'Every public endpoint has a reference page generated from the OpenAPI spec.', 5 * 1000, {
      sessionIds: ['6b9f0e3a-openapi'],
      // ADR 0049 decision 5: Codex refused on its usage limit; the scheduler moved it to Claude Code.
      launches: [
        launch(M.mbp, now - 15 * MIN, null, { by: 'scheduler', agentKind: 'codex', model: null, result: 'error', message: 'You have hit your usage limit · resets 6pm' }),
        launch(M.mbp, now - 14 * MIN, '6b9f0e3a-openapi', { by: 'scheduler', model: 'claude-sonnet-5', note: 'Codex failed (usage limit). The work continues on the same branch.', agentReason: '4 of 5 missions done on this project; about $0.42 a mission' }),
      ],
      placement: { machineId: M.mbp },
      model: 'claude-sonnet-5',
    }, now),
    mission('msn_demo_review', M.orgus, 'itm_demo_review', 'Review the proposed breakdown', 'Imports open in a review where each proposed item is accepted, edited or rejected.', 35 * MIN, {
      sessionIds: ['2e8f7a91-docs-v2'],
      launches: [launch(M.studio, now - 2 * H, '2e8f7a91-docs-v2')],
      closed: { as: 'done', at: now - 35 * MIN, by: 'owner' },
      syncedState: 'done',
      report: { at: now - 35 * MIN, text: 'ACCEPTANCE REPORT\nAC-1: PASS - ProposalReview splits at 1440 px\nAC-2: PASS - apply.test.ts covers a rejected parent\nAC-3: FAIL - sections are not yet kept on tasks', acceptance: [] },
    }, now),
    mission('msn_demo_guide', M.docs, 'itm_demo_guide', 'Rewrite the getting started guide', 'The v2 guide works end to end on a fresh machine, with current screenshots.', 2 * H, {
      sessionIds: ['2e8f7a91-docs-v2'],
      launches: [launch(M.mbp, now - 5 * H, '2e8f7a91-docs-v2')],
      closed: { as: 'done', at: now - 2 * H, by: 'owner' },
    }, now),
    mission('msn_demo_pricing', M.qawl, 'itm_demo_pricing', 'Fix $0 run pricing', 'Runs are priced from usage rows again, and last week is backfilled.', 25 * H, {
      sessionIds: ['8a0d3f6b-pricing'],
      launches: [launch(M.studio, now - 27 * H, '8a0d3f6b-pricing')],
    }, now),
    mission('msn_demo_deps', M.billing, 'itm_demo_deps', 'Nightly dependency upgrade', 'Dependencies are on their latest minor versions and the tests pass.', 4 * H, {
      launches: [launch(M.build, now - 4 * H, null, { result: 'error', message: 'Claude Code stopped before the session began (exit 1). Not logged in: run claude login on build-01.' })],
      placement: { machineId: M.build },
    }, now),
    mission('msn_demo_voice', M.qawl, 'itm_demo_voice', 'Cut voice mint latency', 'Minting a Gemini Live token takes under one second at p95.', 5 * H, { budget: 8 }, now),
    mission('msn_demo_sessions_page', M.orgus, 'itm_demo_sessions_page', 'Sessions page with grouped rows', 'Rows group by state and the drawer opens on Enter.', 12 * 1000, {
      sessionIds: ['f0c9e1a2-sessions-page'],
      launches: [launch(M.studio, now - 26 * MIN, 'f0c9e1a2-sessions-page', { checkoutId: 'c-fd-main' })],
      placement: { machineId: M.studio },
      budget: 15,
      syncedState: 'running',
    }, now),
    mission('msn_demo_sdk_v18', M.billing, 'itm_demo_sdk_v18', 'Upgrade the payments SDK', 'The SDK is on v18, the types compile and the webhook tests pass.', 5 * H + 40 * MIN, {
      sessionIds: ['0199e0c0-codex-stripe'],
      launches: [launch(M.build, now - 6 * H, '0199e0c0-codex-stripe', { checkoutId: 'c-bill-build', worktree: '/srv/checkouts/billing-service-wt/stripe-v18' })],
      placement: { machineId: M.build },
      syncedState: 'done',
      landed: { branch: 'mission/stripe-sdk-v18', sha: '4be91c2', sessionId: '0199e0c0-codex-stripe', at: now - 5 * H - 40 * MIN, by: 'orgus' },
      closed: { as: 'done', at: now - 5 * H - 40 * MIN, by: 'orgus', note: 'Landed mission/stripe-sdk-v18 at 4be91c2.' },
      report: {
        at: now - 5 * H - 40 * MIN,
        text: 'ACCEPTANCE REPORT\nAC-1: PASS - tsc clean on v18\nAC-2: PASS - 14 of 14 webhook tests pass\nAC-3: FAIL - the changelog entry is missing',
        acceptance: [
          { id: 'ac1', met: true, evidence: 'tsc clean on v18' },
          { id: 'ac2', met: true, evidence: '14 of 14 webhook tests pass' },
          { id: 'ac3', met: false, evidence: 'the changelog entry is missing' },
        ],
      },
    }, now),
  ];

  const importProposal: Proposal = {
    id: DEMO.importProposal,
    kind: 'import',
    projectId: M.billing,
    title: 'Import of payments-spec.md',
    by: 'srikanth',
    createdAt: now - 4 * MIN,
    updatedAt: now - 4 * MIN,
    jobId: 'job_demo_import_done',
    status: 'open',
    items: [
      proposed('E1', '', 'epic', 'Take payments for orders', 'Customers pay by card or bank transfer, and every payment is recorded against its order.', ['S1'], [], { priority: 1 }),
      proposed('F1', 'E1', 'feature', 'Card payments', 'Charge cards when the order is placed, and recover from declines.', ['S1.1']),
      proposed('S1', 'F1', 'story', 'Pay for an order by card', 'As a customer, I want to pay by card when I place an order, so that it ships without delay.', ['S1.1'], [gwt('an order in the basket', 'I pay with a valid card', 'the order is placed and shows as paid'), gwt('a card the bank declines', 'I pay', 'I see the reason the bank gave and can try another card')], { priority: 1, estimate: 5 }),
      proposed('F2', 'E1', 'feature', 'Refunds', 'Support refunds paid orders in full or in part.', ['S1.2']),
      proposed('S2', 'F2', 'story', 'Refund an order in full or in part', 'As a support agent, I want to refund a paid order within 30 days, so that customers get their money back.', ['S1.2'], [gwt('an order paid 10 days ago', 'I refund 20 of its 50', 'the customer is credited 20 and the order shows a partial refund'), gwt('an order paid 31 days ago', 'I try to refund it', 'the refund is refused with the 30-day reason')], { estimate: 5 }),
      proposed('S3', 'F2', 'story', 'Email the customer about a refund', 'As a customer, I want an email when I am refunded, so that I know the amount and when it arrives.', ['S1.2'], [gwt('a refund is issued', 'it is saved', 'the customer gets an email with the amount and the expected date within 5 minutes')], { estimate: 2 }),
      proposed('E2', '', 'epic', 'Receipts', 'Every paid order has a receipt the customer can download.', ['S2']),
      proposed('S4', 'E2', 'story', 'Download a PDF receipt', 'As a customer, I want a PDF receipt for each paid order, so that I can claim expenses.', ['S2'], [gwt('a paid order', 'I open it', 'a Download receipt button gives a PDF with tax per line')], { estimate: 3 }),
    ],
    edits: [],
    links: [],
    questions: ['Which format does finance need for the monthly export: CSV, Excel, or their accounting system? (S3)', 'Are bank transfers refunded the same way as cards? (S1.2)'],
    assumptions: ['Prices include tax, and tax per line is already known at checkout.', 'Partial refunds can be repeated until the order total is refunded.'],
    source: { name: 'payments-spec.md', sections: SPEC_SECTIONS, chars: 2410 },
    targetItemId: null,
  };

  const signals: DefectSignal[] = [
    { id: 'sig_demo_job', kind: 'job', ref: 'job:m-build01:j-77', projectId: M.billing, machineId: M.build, title: '`pnpm test` failed with exit code 1', evidence: 'Command: pnpm test\nExit code: 1\nFolder: /srv/billing-service\nBranch: main\nMachine: build-01', at: now - 25 * MIN, status: 'open' },
    { id: 'sig_demo_verify', kind: 'verify', ref: 'pipe:p-7f3a91c2:land-1142', projectId: M.qawl, machineId: M.studio, title: 'Verify failed: land #1142 approvals-routing', evidence: 'Pipeline: Landing queue (qawl-land)\nItem: land #1142 approvals-routing\nStage: Verify\nDetail: 2 tests failed in routing.test.ts', at: now - 50 * MIN, status: 'open' },
    { id: 'sig_demo_session', kind: 'session', ref: 'session:demo-err', projectId: M.orgus, machineId: M.studio, title: 'Session ended on an error: There is an issue with the selected model (claude-haiku-4.5)', evidence: 'Session: demo-err\nModel: claude-haiku-4.5\nLast message: There is an issue with the selected model (claude-haiku-4.5). It may not exist or you may not have access to it.', at: now - 2 * H, status: 'open' },
  ];

  const keys: Record<Id, ProjectKey> = { [M.qawl]: { prefix: 'QAWL', next: 12 }, [M.orgus]: { prefix: 'AIR', next: 13 }, [M.billing]: { prefix: 'BILL', next: 3 }, [M.docs]: { prefix: 'DOCS', next: 4 } };
  const views: SavedView[] = [
    { id: 'vw_demo_triage', name: 'Defects to triage', projectId: null, view: 'defects', filter: { kinds: ['defect'], statuses: ['triage'] }, by: 'srikanth', createdAt: now - 3 * D },
    { id: 'vw_demo_attention', name: 'Needs attention', projectId: null, view: 'table', filter: { issues: true }, by: 'srikanth', createdAt: now - 2 * D },
  ];
  // The mission run page's phases the rest does not reach: preparing, checking, landed (run/mock.ts).
  items.push(...runDemoItems(now));
  missions.push(...runDemoMissions(now));
  runDemoReview(items, missions, now);
  keys[M.qawl] = { prefix: 'QAWL', next: 13 };
  return { items, missions, links, keys, views, proposals: [importProposal], signals, sequences: [] };
}

// ---------------------------------------------------------------------------
// The in-browser stand-in for the hub API
// ---------------------------------------------------------------------------

export class MockRefusal extends Error {}

function nextKey(s: MissionsState, projectId: Id, projectName = ''): { key: string; keys: Record<Id, ProjectKey> } {
  const keys = { ...(s.keys ?? {}) };
  const k = keys[projectId] ?? { prefix: keyPrefixFor(projectName || projectId, new Set(Object.values(keys).map((x) => x.prefix))), next: 1 };
  keys[projectId] = { ...k, next: k.next + 1 };
  return { key: formatKey(k.prefix, k.next), keys };
}

export function mockCreateItem(s: MissionsState, input: ItemInput, now: number): { state: MissionsState; item: BacklogItem } {
  const err = itemInputError(input);
  if (err) throw new MockRefusal(err);
  const kind = normalizeKind(input.kind)!;
  const { key, keys } = nextKey(s, input.projectId);
  const it: BacklogItem = {
    id: newPrefixedId('itm', now),
    key,
    projectId: input.projectId,
    kind,
    title: input.title.trim(),
    body: input.body ?? '',
    status: input.status ? fitStatus(kind, input.status) : initialStatus(kind),
    priority: input.priority ?? 2,
    owner: input.owner?.trim() || null,
    parentId: input.parentId ?? null,
    rank: input.rank ?? now,
    acceptance: normalizeCriteria(input.acceptance ?? []),
    estimate: input.estimate ?? null,
    labels: input.labels ?? [],
    components: input.components ?? [],
    defect: kind === 'defect' ? { severity: 'major', environment: '', steps: '', expected: '', actual: '', ...(input.defect ?? {}) } : null,
    source: input.source ?? null,
    aiDrafted: input.aiDrafted ?? false,
    statusNote: null,
    statusPinned: false,
    createdAt: now,
    updatedAt: now,
  };
  return { state: { ...s, keys, items: [it, ...s.items] }, item: it };
}

export function mockUpdateItem(s: MissionsState, id: Id, patch: ItemPatch, now: number): { state: MissionsState; item: BacklogItem } {
  const err = itemInputError(patch, true);
  if (err) throw new MockRefusal(err);
  const prev = s.items.find((i) => i.id === id);
  if (!prev) throw new MockRefusal('That backlog item is not known to this hub.');
  const kind = patch.kind ? normalizeKind(patch.kind)! : prev.kind;
  if (patch.status && !ITEM_WORKFLOW[kind].includes(patch.status)) throw new MockRefusal(`${kindWord(kind, true)} cannot be ${statusLabel(kind, patch.status).toLowerCase()}.`);
  const { override, pinned, acceptance, defect, ...rest } = patch;
  const next: BacklogItem = {
    ...prev,
    ...rest,
    kind,
    ...(patch.status === undefined && kind !== prev.kind ? { status: fitStatus(kind, prev.status) } : {}),
    ...(patch.title !== undefined ? { title: patch.title.trim() } : {}),
    ...('owner' in patch ? { owner: patch.owner?.trim() || null } : {}),
    ...('parentId' in patch ? { parentId: patch.parentId ?? null } : {}),
    ...(acceptance ? { acceptance: normalizeCriteria(acceptance, prev.acceptance) } : {}),
    defect: kind === 'defect' ? { ...(prev.defect ?? { severity: 'major', environment: '', steps: '', expected: '', actual: '' }), ...(defect ?? {}) } : null,
    ...(override ? { statusPinned: true, statusNote: { text: override.reason, by: 'owner', at: now, kind: 'override' as const } } : patch.status !== undefined ? { statusPinned: false, statusNote: null } : {}),
    ...(pinned === false ? { statusPinned: false } : {}),
    updatedAt: now,
  };
  return { state: { ...s, items: s.items.map((i) => (i.id === id ? next : i)) }, item: next };
}

export function mockDeleteItem(s: MissionsState, id: Id): MissionsState {
  const children = s.items.filter((i) => i.parentId === id).length;
  if (children) throw new MockRefusal(`It has ${children} child ${children === 1 ? 'item' : 'items'}. Move or delete them first, or set the item to Dropped.`);
  // As the hub: a done or dropped item goes with its closed missions; an open one keeps them (R-FN-8).
  const linked = s.missions.filter((m) => m.itemId === id);
  const it = s.items.find((i) => i.id === id);
  const open = linked.filter((m) => !m.closed).length;
  if (open) throw new MockRefusal(`${open} ${open === 1 ? 'mission is' : 'missions are'} still open on it. Close the mission as done or stopped, then delete the item.`);
  if (linked.length && it?.status !== 'done' && it?.status !== 'dropped') throw new MockRefusal('Missions link to it, and the item is not done. Set the item to Done or Dropped, then delete it with its missions.');
  return { ...s, items: s.items.filter((i) => i.id !== id), missions: s.missions.filter((m) => m.itemId !== id), links: (s.links ?? []).filter((l) => l.from !== id && l.to !== id) };
}

export function mockCheckCriterion(s: MissionsState, id: Id, cid: string, done: boolean, now: number): MissionsState {
  return { ...s, items: s.items.map((i) => (i.id === id ? { ...i, acceptance: i.acceptance.map((c) => (c.id === cid ? { ...c, done, checkedBy: done ? 'owner' : null, checkedAt: done ? now : null } : c)), updatedAt: now } : i)) };
}

export function mockLink(s: MissionsState, input: LinkInput, now: number): { state: MissionsState; link: ItemLink } {
  const err = linkInputError(input);
  if (err) throw new MockRefusal(err);
  const c = canonicalLink(input);
  const link: ItemLink = { id: newPrefixedId('lnk', now), type: c.type as LinkType, from: c.from, to: c.to, by: 'owner', at: now };
  return { state: { ...s, links: [...(s.links ?? []), link] }, link };
}

export function mockUnlink(s: MissionsState, id: Id): MissionsState {
  return { ...s, links: (s.links ?? []).filter((l) => l.id !== id) };
}

export function mockSaveView(s: MissionsState, input: SavedViewInput, now: number): { state: MissionsState; view: SavedView } {
  if (!input.name.trim()) throw new MockRefusal('Name the view.');
  const existing = (s.views ?? []).find((v) => v.name.toLowerCase() === input.name.trim().toLowerCase() && v.projectId === input.projectId);
  const view: SavedView = { id: existing?.id ?? newPrefixedId('vw', now), name: input.name.trim(), projectId: input.projectId, view: input.view, filter: input.filter, ...(input.sort ? { sort: input.sort } : {}), by: 'owner', createdAt: existing?.createdAt ?? now };
  return { state: { ...s, views: [...(s.views ?? []).filter((v) => v.id !== view.id), view] }, view };
}

export function mockDeleteView(s: MissionsState, id: Id): MissionsState {
  return { ...s, views: (s.views ?? []).filter((v) => v.id !== id) };
}

export function mockSetPrefix(s: MissionsState, projectId: Id, prefix: string): MissionsState {
  const keys = { ...(s.keys ?? {}) };
  keys[projectId] = { prefix, next: keys[projectId]?.next ?? 1 };
  return { ...s, keys, items: s.items.map((i) => (i.projectId === projectId ? { ...i, key: i.key.replace(/^[A-Z0-9]+-/, `${prefix}-`) } : i)) };
}

export function mockCreateMission(s: MissionsState, input: MissionInput, owner: string, now: number): { state: MissionsState; mission: Mission } {
  const err = missionInputError(input);
  if (err) throw new MockRefusal(err);
  const m: Mission = {
    id: newPrefixedId('msn', now),
    itemId: input.itemId ?? null,
    projectId: input.projectId,
    title: input.title.trim(),
    goal: input.goal.trim(),
    owner: input.owner ?? owner,
    state: 'planned',
    progress: 0,
    sessionIds: [],
    launches: [],
    placement: input.placement ?? {},
    model: input.model ?? null,
    budget: input.budget ?? null,
    closed: null,
    syncedState: null,
    report: null,
    sequenceId: null,
    createdAt: now,
    updatedAt: now,
  };
  return { state: { ...s, missions: [m, ...s.missions] }, mission: m };
}

export function mockUpdateMission(s: MissionsState, id: Id, patch: MissionPatch, by: string, now: number): { state: MissionsState; mission: Mission } {
  const err = missionInputError(patch, true);
  if (err) throw new MockRefusal(err);
  const prev = s.missions.find((m) => m.id === id);
  if (!prev) throw new MockRefusal('That mission is not known to this hub.');
  const { closed, ...rest } = patch;
  const next: Mission = {
    ...prev,
    ...rest,
    ...(closed !== undefined ? { closed: closed ? { as: closed, at: now, by } : null } : {}),
    updatedAt: now,
  };
  // The item follows a closed mission: in review when done, back to ready when stopped.
  const items = closed && prev.itemId
    ? s.items.map((i) => (i.id === prev.itemId && !i.statusPinned && i.status !== 'done' && i.status !== 'dropped' ? { ...i, status: closed === 'done' ? fitStatus(i.kind, 'review') : fitStatus(i.kind, 'ready'), statusNote: { text: closed === 'done' ? `Mission "${prev.title}" finished. Review what it changed.` : `Mission "${prev.title}" was stopped: stopped by ${by}`, by: 'orgus', at: now, kind: 'mission' as const, missionId: id }, updatedAt: now } : i))
    : s.items;
  return { state: { ...s, items, missions: s.missions.map((m) => (m.id === id ? next : m)) }, mission: next };
}

export function mockDeleteMission(s: MissionsState, id: Id): MissionsState {
  const m = s.missions.find((x) => x.id === id);
  if (m && (m.launches.length || m.sessionIds.length)) throw new MockRefusal('It has started sessions, and it keeps their history. Close it as done or stopped instead.');
  return { ...s, missions: s.missions.filter((x) => x.id !== id) };
}

/** Record a start on a mission; its item follows it to in progress. */
export function mockRecordLaunch(s: MissionsState, id: Id, launch: MissionLaunch, patch: Partial<Pick<Mission, 'budget' | 'model'>>, now: number): { state: MissionsState; mission: Mission } {
  const prev = s.missions.find((m) => m.id === id);
  if (!prev) throw new MockRefusal('That mission is not known to this hub.');
  const next: Mission = {
    ...prev,
    ...patch,
    launches: [...prev.launches, launch],
    sessionIds: launch.sessionId && !prev.sessionIds.includes(launch.sessionId) ? [...prev.sessionIds, launch.sessionId] : prev.sessionIds,
    placement: { ...prev.placement, machineId: launch.machineId },
    syncedState: launch.result === 'ok' ? 'running' : prev.syncedState ?? null,
    updatedAt: now,
  };
  const items =
    launch.result === 'ok' && prev.itemId
      ? s.items.map((i) => (i.id === prev.itemId && i.status !== 'done' && i.status !== 'dropped' ? { ...i, status: fitStatus(i.kind, 'in_progress'), statusPinned: false, statusNote: { text: `Mission "${prev.title}" is working on it.`, by: 'orgus', at: now, kind: 'mission' as const, missionId: id }, updatedAt: now } : i))
      : s.items;
  return { state: { ...s, items, missions: s.missions.map((m) => (m.id === id ? next : m)) }, mission: next };
}
